import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Armchair, Camera, Gamepad2, ArrowLeft, Plus } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { fileUrl } from "../components/site/HeroSlides";

const TABS = [
  { id: "seat", label: "Seating", icon: Armchair },
  { id: "photos", label: "Table photos", icon: Camera },
  { id: "games", label: "Games", icon: Gamepad2 },
];

function Seating({ d, slug }) {
  if (!d.seating_public) return <p className="text-sm text-[var(--muted)]" data-testid="table-seating-private">The couple hasn't published the seating chart yet. Ask a host to find your seat.</p>;
  return (
    <div data-testid="table-seating">
      <p className="text-sm text-[var(--muted)] mb-3">Seated at {d.table.name}</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {d.guests.map((n) => <span key={n} className="px-3 py-1.5 rounded-full bg-white border border-[var(--line)] text-sm">{n}</span>)}
        {!d.guests.length && <p className="text-sm text-[var(--muted)]">No one is seated here yet.</p>}
      </div>
      <Link to={`/site/${slug}/seating`} className="btn-outline-gold text-xs" data-testid="table-full-chart">See the full seating chart</Link>
    </div>
  );
}

function Photos({ d, slug, tableId }) {
  return (
    <div data-testid="table-photos">
      <Link to={`/site/${slug}/upload?table=${tableId}`} className="btn-gold text-xs mb-5 inline-flex" data-testid="table-add-photo"><Plus className="w-3 h-3" /> Add a photo from {d.table.name}</Link>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {d.photos.map((p) => (
          <figure key={p.item_id} className="relative aspect-square rounded-xl overflow-hidden bg-black/5">
            {p.type === "video" ? <video src={fileUrl(p.path)} className="w-full h-full object-cover" muted playsInline controls /> : <img src={fileUrl(p.path)} alt={p.caption} className="w-full h-full object-cover" />}
            {p.by && <figcaption className="absolute bottom-0 inset-x-0 text-[10px] text-white bg-gradient-to-t from-black/70 to-transparent px-2 py-1">{p.by}</figcaption>}
          </figure>
        ))}
      </div>
      {!d.photos.length && <p className="text-sm text-[var(--muted)]">Be the first to share a photo from this table.</p>}
    </div>
  );
}

function Games({ d }) {
  if (!d.games.length) return <p className="text-sm text-[var(--muted)]">No games are open right now. Check back soon!</p>;
  return (
    <div className="space-y-2" data-testid="table-games">
      {d.games.map((g) => (
        <Link key={g.code} to={g.href} className="flex items-center justify-between bg-white border border-[var(--line)] rounded-xl px-4 py-3 hover:border-[var(--gold)] transition-colors" data-testid={`table-game-${g.code}`}>
          <span className="font-medium">{g.title}</span><span className="text-[10px] uppercase tracking-widest text-[var(--gold)]">Play</span>
        </Link>
      ))}
    </div>
  );
}

export default function TableHubPage() {
  const { slug, tableId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("seat");
  useEffect(() => { api.get(`/public/weddings/${slug}/table/${tableId}`).then((r) => setD(r.data)).catch((e) => setErr(formatApiError(e))); }, [slug, tableId]);

  if (err) return <div className="min-h-screen flex items-center justify-center p-6 text-center text-[var(--muted)]" data-testid="table-hub-error">{err}</div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin" /></div>;
  return (
    <div className="min-h-screen bg-[var(--bg)] px-5 py-10" data-testid="table-hub">
      <div className="max-w-xl mx-auto">
        <Link to={`/site/${slug}`} className="text-xs text-[var(--muted)] inline-flex items-center gap-1 mb-6"><ArrowLeft className="w-3 h-3" /> {d.couple_names}</Link>
        <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--gold)]">Welcome to</p>
        <h1 className="font-serif font-light text-5xl mb-8" data-testid="table-hub-name">{d.table.name}</h1>
        <div className="grid grid-cols-3 gap-2 mb-8">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`table-tab-${id}`}
              className={`flex flex-col items-center gap-2 py-5 rounded-2xl border transition-colors ${tab === id ? "bg-[#111] text-white border-[#111]" : "bg-white border-[var(--line)] hover:border-[var(--gold)]"}`}>
              <Icon className="w-5 h-5" /><span className="text-xs">{label}</span>
            </button>
          ))}
        </div>
        {tab === "seat" && <Seating d={d} slug={slug} />}
        {tab === "photos" && <Photos d={d} slug={slug} tableId={tableId} />}
        {tab === "games" && <Games d={d} />}
      </div>
    </div>
  );
}
